/**
 * Privacy-friendly visitor analytics for 6amico (GDPR).
 *
 * Dual setup, both gated by env (no hard-coded production IDs):
 * - Cloudflare Web Analytics (REACT_APP_CF_ANALYTICS_TOKEN) — cookieless beacon;
 *   preferred for geo aggregates (country/region) without client geo-IP APIs.
 * - Google Analytics 4 (REACT_APP_GA_MEASUREMENT_ID) — Consent Mode v2, IP anonymized,
 *   no ads; custom events for section clicks and board dwell time.
 *
 * Geo (region/country) and averages come from the CF/GA dashboards (server-side IP
 * aggregation). Do not call third-party geo-IP APIs from the client.
 *
 * Enable in deploy env, then redeploy frontend:
 *   REACT_APP_GA_MEASUREMENT_ID=G-XXXXXXXX
 *   REACT_APP_CF_ANALYTICS_TOKEN=<cloudflare-web-analytics-token>
 */

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const GA_ID = (process.env.REACT_APP_GA_MEASUREMENT_ID || "").trim();
const CF_TOKEN = (process.env.REACT_APP_CF_ANALYTICS_TOKEN || "").trim();
const CONSENT_LS_KEY = "6amico_analytics_consent";

function analyticsApiBase() {
  const raw = (process.env.REACT_APP_BACKEND_URL || "").trim().replace(/\/+$/, "");
  if (!raw) return "/api";
  if (/^https?:\/\//i.test(raw)) return `${raw}/api`;
  return `http://${raw.replace(/^\/+/, "")}/api`;
}

/** Canonical section ids for `section_click` events. */
export const ANALYTICS_SECTIONS = Object.freeze({
  monumenti: "monumenti",
  sentieri_cai: "sentieri_cai",
  aree_camper: "aree_camper",
  mappa: "mappa",
  percorsi: "percorsi",
  frazioni: "frazioni",
  mangiare: "mangiare",
  dormire: "dormire",
  prodotti: "prodotti",
  luoghi: "luoghi",
  natura: "natura",
  proloco: "proloco",
  storia: "storia",
  mare: "mare",
  terra: "terra",
  montagna: "montagna",
  bici: "bici",
  cultura: "cultura",
});

const TAB_TO_SECTION = {
  luoghi: ANALYTICS_SECTIONS.monumenti,
  "cosa-vedere": ANALYTICS_SECTIONS.monumenti,
  musei: ANALYTICS_SECTIONS.monumenti,
  percorsi: ANALYTICS_SECTIONS.sentieri_cai,
  natura: ANALYTICS_SECTIONS.natura,
  frazioni: ANALYTICS_SECTIONS.frazioni,
  mangiare: ANALYTICS_SECTIONS.mangiare,
  dormire: ANALYTICS_SECTIONS.dormire,
  prodotti: ANALYTICS_SECTIONS.prodotti,
  proloco: ANALYTICS_SECTIONS.proloco,
  storia: ANALYTICS_SECTIONS.storia,
  sentieri: ANALYTICS_SECTIONS.sentieri_cai,
  MONUMENTI: ANALYTICS_SECTIONS.monumenti,
  BIKE: ANALYTICS_SECTIONS.bici,
  HOTEL: ANALYTICS_SECTIONS.dormire,
};

let initialized = false;
let gaReady = false;

function readStoredConsent() {
  try {
    const v = localStorage.getItem(CONSENT_LS_KEY);
    if (v === "1" || v === "true") return true;
    if (v === "0" || v === "false") return false;
  } catch {
    /* ignore */
  }
  return null;
}

/** No public cookie banner: Cookie Policy already discloses anonymized GA → default allow analytics. */
function analyticsConsentGranted() {
  const stored = readStoredConsent();
  if (stored === false) return false;
  if (stored === true) return true;
  return Boolean(GA_ID);
}

function gtag(...args) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
}

function injectScript(src, attrs = {}) {
  if (typeof document === "undefined") return null;
  if (document.querySelector(`script[src="${src}"]`)) return null;
  const s = document.createElement("script");
  s.src = src;
  s.async = true;
  Object.entries(attrs).forEach(([k, v]) => {
    if (v != null) s.setAttribute(k, v);
  });
  document.head.appendChild(s);
  return s;
}

function initCloudflare() {
  if (!CF_TOKEN || typeof document === "undefined") return;
  if (document.querySelector("script[data-cf-beacon]")) return;
  const s = document.createElement("script");
  s.defer = true;
  s.src = "https://static.cloudflareinsights.com/beacon.min.js";
  s.setAttribute("data-cf-beacon", JSON.stringify({ token: CF_TOKEN }));
  document.head.appendChild(s);
}

function initGa4() {
  if (!GA_ID || typeof window === "undefined") return;
  if (gaReady) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || gtag;

  const granted = analyticsConsentGranted();
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: granted ? "granted" : "denied",
    functionality_storage: "granted",
    security_storage: "granted",
    wait_for_update: 500,
  });

  injectScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`);
  gtag("js", new Date());
  gtag("config", GA_ID, {
    anonymize_ip: true,
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
  gaReady = true;
}

/** Call once on app boot (safe if env vars empty — no-op). */
export function initAnalytics() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  initCloudflare();
  initGa4();
}

export function setAnalyticsConsent(granted) {
  try {
    localStorage.setItem(CONSENT_LS_KEY, granted ? "1" : "0");
  } catch {
    /* ignore */
  }
  if (!GA_ID || typeof window === "undefined") return;
  if (!gaReady) initGa4();
  gtag("consent", "update", {
    analytics_storage: granted ? "granted" : "denied",
    ad_storage: "denied",
  });
}

export function trackPageView(path, title) {
  if (!GA_ID || !gaReady || !analyticsConsentGranted()) return;
  gtag("event", "page_view", {
    page_path: path,
    page_title: title || (typeof document !== "undefined" ? document.title : ""),
    send_to: GA_ID,
  });
}

export function trackEvent(name, params = {}) {
  if (!GA_ID || !gaReady || !analyticsConsentGranted()) return;
  const safe = { ...params };
  // Never send emails, names, or free-text that may contain PII
  delete safe.email;
  delete safe.user_id;
  delete safe.name;
  gtag("event", name, safe);
}

/**
 * Map hub tab / map filter / layer id → canonical section for dashboards.
 * @param {string} raw
 */
export function normalizeSectionId(raw) {
  if (!raw) return "unknown";
  const key = String(raw);
  if (TAB_TO_SECTION[key]) return TAB_TO_SECTION[key];
  const lower = key.toLowerCase();
  if (ANALYTICS_SECTIONS[lower]) return ANALYTICS_SECTIONS[lower];
  if (TAB_TO_SECTION[lower]) return TAB_TO_SECTION[lower];
  if (lower === "sentieri" || lower.includes("sentier")) return ANALYTICS_SECTIONS.sentieri_cai;
  if (lower.includes("camper")) return ANALYTICS_SECTIONS.aree_camper;
  if (lower.includes("monument")) return ANALYTICS_SECTIONS.monumenti;
  return lower.replace(/\s+/g, "_").slice(0, 64);
}

/**
 * Fire-and-forget POST to 6amico metrics (GDPR: no PII).
 * Used so admin Analytics can read section/monument breakdowns.
 */
export function postAnalyticsEvent(payload) {
  if (!payload || typeof payload !== "object" || typeof fetch === "undefined") return;
  const body = { ...payload };
  delete body.email;
  delete body.user_id;
  delete body.name;
  try {
    fetch(`${analyticsApiBase()}/analytics/event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => null);
  } catch {
    /* ignore */
  }
}

/**
 * Territory engagement → GA4 (if configured) + backend metrics.
 * Prefer this inside comune/monument UI so the dashboard can unpack visits.
 *
 * @param {{ comuneSlug?: string, monumentId?: string, section?: string, label?: string, path?: string, source?: string }} opts
 */
export function trackTerritoryEngagement(opts = {}) {
  const {
    comuneSlug,
    monumentId,
    section,
    label,
    path,
    source,
  } = opts;
  const sec = normalizeSectionId(
    section || (monumentId ? ANALYTICS_SECTIONS.monumenti : "unknown"),
  );
  const pathVal =
    path ||
    (typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search || ""}`
      : undefined);

  trackEvent("section_click", {
    section: sec,
    source: source || undefined,
    comune_slug: comuneSlug || undefined,
    monument_id: monumentId || undefined,
    label: label || undefined,
  });

  const kind = monumentId ? "monument_view" : "section_click";
  postAnalyticsEvent({
    kind,
    entity_type: monumentId ? "monument" : "section",
    entity_slug: comuneSlug || undefined,
    entity_id: monumentId || undefined,
    comune_slug: comuneSlug || undefined,
    monument_id: monumentId || undefined,
    section: sec,
    label: label ? String(label).slice(0, 200) : undefined,
    path: pathVal ? String(pathVal).slice(0, 500) : undefined,
  });
}

/** High-traffic CTA / tab / map channel click (GA + backend when territorial). */
export function trackSectionClick(section, extra = {}) {
  const sec = normalizeSectionId(section);
  trackEvent("section_click", {
    section: sec,
    ...extra,
  });
  const comuneSlug = extra.comune_slug || extra.comuneSlug;
  const monumentId = extra.monument_id || extra.monumentId;
  if (comuneSlug || monumentId) {
    postAnalyticsEvent({
      kind: monumentId ? "monument_view" : "section_click",
      entity_type: monumentId ? "monument" : "section",
      entity_slug: comuneSlug || undefined,
      entity_id: monumentId || undefined,
      comune_slug: comuneSlug || undefined,
      monument_id: monumentId || undefined,
      section: sec,
      label: extra.label ? String(extra.label).slice(0, 200) : undefined,
      path:
        typeof window !== "undefined"
          ? `${window.location.pathname}${window.location.search || ""}`.slice(0, 500)
          : undefined,
    });
  }
}

/**
 * SPA page views on React Router navigation.
 */
export function AnalyticsRouteListener() {
  const location = useLocation();
  const first = useRef(true);

  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    const path = `${location.pathname}${location.search}`;
    // Skip duplicate on Strict Mode remount of first paint when possible
    if (first.current) {
      first.current = false;
    }
    trackPageView(path);
  }, [location.pathname, location.search]);

  return null;
}

/**
 * Visible dwell time on monument boards / comune pages.
 * Fires `monument_engagement` or `comune_board_time` on unmount / hide (≥1s).
 */
export function useBoardEngagement(eventName, params = {}) {
  const paramsKey = JSON.stringify(params || {});
  const startedRef = useRef(Date.now());
  const accumulatedRef = useRef(0);
  const visibleSinceRef = useRef(
    typeof document !== "undefined" && document.visibilityState === "visible"
      ? Date.now()
      : null,
  );

  useEffect(() => {
    startedRef.current = Date.now();
    accumulatedRef.current = 0;
    visibleSinceRef.current =
      document.visibilityState === "visible" ? Date.now() : null;

    const flushVisible = () => {
      if (visibleSinceRef.current != null) {
        accumulatedRef.current += Date.now() - visibleSinceRef.current;
        visibleSinceRef.current = null;
      }
    };

    const onVis = () => {
      if (document.visibilityState === "hidden") {
        flushVisible();
      } else if (visibleSinceRef.current == null) {
        visibleSinceRef.current = Date.now();
      }
    };

    const send = () => {
      flushVisible();
      const seconds = Math.round(accumulatedRef.current / 1000);
      if (seconds < 1 || !eventName) return;
      let parsed = {};
      try {
        parsed = JSON.parse(paramsKey);
      } catch {
        parsed = {};
      }
      trackEvent(eventName, {
        ...parsed,
        engagement_seconds: seconds,
      });
    };

    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", send);

    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", send);
      send();
    };
  }, [eventName, paramsKey]);
}

export function isAnalyticsConfigured() {
  return Boolean(GA_ID || CF_TOKEN);
}

export function getAnalyticsProviders() {
  return {
    ga4: Boolean(GA_ID),
    cloudflare: Boolean(CF_TOKEN),
  };
}
